/**
 * shapeEditorSlice — Redux Toolkit state for the Custom Shape Editor modal.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   activeTool: ShapeEditorTool,
 *   pathData: string | null,          // Paper.js exportJSON output
 *   viewport: ShapeEditorViewport,
 *   historyStack: string[],           // capped at 50 snapshots
 *   forwardStack: string[],           // redo targets
 *   border: ShapeEditorBorder,
 *   validationWarnings: ShapeEditorValidationWarning[],
 *   boundingBox: ShapeEditorBoundingBox,
 *   entryContext: EntryContext | null,
 *   selectedSegments: number[],
 * }
 * ```
 *
 * Key invariants
 * --------------
 * - `pathData` carries the serialised JSON string from `path.exportJSON()`.
 *   The slice never imports Paper.js — callers serialise before dispatching.
 * - `pushHistorySnapshot` appends `pathData` to `historyStack` and clears
 *   `forwardStack`. The stack is capped at 50 via `splice(0, 1)` (safe in
 *   Immer draft context; `shift()` is intentionally avoided).
 * - `updatePathData` is intentionally separate from `pushHistorySnapshot` so
 *   undo/redo restoration and initial path import do not create history entries.
 * - `resetEditor` returns every field to initial state so the modal can be
 *   re-opened with a fresh entry context without stale data leaking.
 *
 * SF-SHAPED-001.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types — exported so capsules / containers reference them without importing
// raw state shape.
// ============================================================================

export type ShapeEditorTool =
  | 'pen'
  | 'select'
  | 'addNode'
  | 'deleteNode'
  | 'scissors'
  | 'simplify'
  | 'pan'
  | 'rectangle'
  | 'square'
  | 'roundedRectangle'
  | 'circle'
  | 'triangle';

export type EntryContext =
  | 'auto_contour_edited'
  | 'drawn_from_scratch'
  | 'preset_edited';

export interface ShapeEditorViewport {
  readonly zoomLevel: number;
  readonly panOffset: { readonly x: number; readonly y: number };
  readonly snapToGrid: boolean;
}

export interface ShapeEditorBorder {
  /** Inches, 0–0.25 */
  readonly thicknessInches: number;
  readonly colorHex: string;
  /** 0–1 */
  readonly cornerSmoothing: number;
}

export interface ShapeEditorValidationWarning {
  readonly rule: string;
  readonly message: string;
}

export interface ShapeEditorBoundingBox {
  readonly widthInches: number | null;
  readonly heightInches: number | null;
}

export interface ShapeEditorState {
  readonly activeTool: ShapeEditorTool;
  readonly pathData: string | null;
  readonly viewport: ShapeEditorViewport;
  readonly historyStack: string[];
  readonly forwardStack: string[];
  readonly border: ShapeEditorBorder;
  readonly validationWarnings: ShapeEditorValidationWarning[];
  readonly boundingBox: ShapeEditorBoundingBox;
  readonly entryContext: EntryContext | null;
  readonly selectedSegments: number[];
  /** Tolerance for path simplification (0.5–10). Persists for the session. */
  readonly simplifyTolerance: number;
  /**
   * Corner radius (inches) the rounded-rectangle shape tool uses for the
   * *next* shape it draws. Bootstraps to a placeholder here — the real,
   * admin-configured default (`artwork.shape_editor_default_corner_radius_in`)
   * is seeded by the container on mount via `setRoundedRectangleCornerRadius`
   * (SH-SHAPE-101). The customer can still change it per-shape afterward.
   */
  readonly roundedRectangleCornerRadiusInches: number;
}

// ============================================================================
// Initial state
// ============================================================================

const initialState: ShapeEditorState = {
  activeTool: 'select',
  pathData: null,
  viewport: {
    zoomLevel: 1.0,
    panOffset: { x: 0, y: 0 },
    snapToGrid: false,
  },
  historyStack: [],
  forwardStack: [],
  border: {
    thicknessInches: 0,
    colorHex: '#FFFFFF',
    cornerSmoothing: 0.5,
  },
  validationWarnings: [],
  boundingBox: {
    widthInches: null,
    heightInches: null,
  },
  entryContext: null,
  selectedSegments: [],
  simplifyTolerance: 2.5,
  roundedRectangleCornerRadiusInches: 0.25,
};

const HISTORY_CAP = 50;

// ============================================================================
// Slice
// ============================================================================

const shapeEditorSlice = createSlice({
  name: 'shapeEditor',
  initialState,
  reducers: {
    setActiveTool(state, action: PayloadAction<ShapeEditorTool>) {
      state.activeTool = action.payload;
    },

    /**
     * Snapshot the current pathData onto historyStack and clear forwardStack.
     * No-op when pathData is null (nothing to snapshot).
     * Cap enforced via splice — safe in Immer's draft context.
     */
    pushHistorySnapshot(state) {
      if (state.pathData === null) return;
      state.historyStack.push(state.pathData);
      if (state.historyStack.length > HISTORY_CAP) {
        state.historyStack.splice(0, 1);
      }
      state.forwardStack = [];
    },

    /**
     * Restore the previous snapshot. Pushes current pathData onto forwardStack
     * first so redo can reverse the operation. No-op when historyStack is empty.
     */
    undo(state) {
      if (state.historyStack.length === 0) return;
      const previous = state.historyStack[state.historyStack.length - 1];
      state.historyStack.splice(state.historyStack.length - 1, 1);
      if (state.pathData !== null) {
        state.forwardStack.push(state.pathData);
      }
      state.pathData = previous;
    },

    /**
     * Re-apply a previously undone snapshot. No-op when forwardStack is empty.
     */
    redo(state) {
      if (state.forwardStack.length === 0) return;
      const next = state.forwardStack[state.forwardStack.length - 1];
      state.forwardStack.splice(state.forwardStack.length - 1, 1);
      if (state.pathData !== null) {
        state.historyStack.push(state.pathData);
      }
      state.pathData = next;
    },

    /** Replace pathData without touching history stacks. */
    updatePathData(state, action: PayloadAction<string | null>) {
      state.pathData = action.payload;
    },

    /** Shallow partial merge into border sub-object. */
    updateBorder(state, action: PayloadAction<Partial<ShapeEditorBorder>>) {
      state.border = { ...state.border, ...action.payload };
    },

    /** Replace viewport entirely. */
    setViewport(state, action: PayloadAction<ShapeEditorViewport>) {
      state.viewport = action.payload;
    },

    /** Update only zoomLevel; leaves panOffset and snapToGrid untouched. */
    setZoom(state, action: PayloadAction<number>) {
      state.viewport = { ...state.viewport, zoomLevel: action.payload };
    },

    /** Update only panOffset. */
    setPan(state, action: PayloadAction<{ x: number; y: number }>) {
      state.viewport = { ...state.viewport, panOffset: action.payload };
    },

    /** Toggle grid snapping. */
    setSnapToGrid(state, action: PayloadAction<boolean>) {
      state.viewport = { ...state.viewport, snapToGrid: action.payload };
    },

    /** Replace bounding box dimensions. */
    setBoundingBox(state, action: PayloadAction<ShapeEditorBoundingBox>) {
      state.boundingBox = action.payload;
    },

    /** Record how the editor was opened. */
    setEntryContext(state, action: PayloadAction<EntryContext | null>) {
      state.entryContext = action.payload;
    },

    /** Replace the warnings array atomically. */
    setValidationWarnings(
      state,
      action: PayloadAction<ShapeEditorValidationWarning[]>,
    ) {
      state.validationWarnings = action.payload;
    },

    /** Replace the selected segment index list. */
    setSelectedSegments(state, action: PayloadAction<number[]>) {
      state.selectedSegments = action.payload;
    },

    /** Add a segment index if absent; remove it if already present. */
    toggleSelectedSegment(state, action: PayloadAction<number>) {
      const idx = state.selectedSegments.indexOf(action.payload);
      if (idx === -1) {
        state.selectedSegments.push(action.payload);
      } else {
        state.selectedSegments.splice(idx, 1);
      }
    },

    clearSelectedSegments(state) {
      state.selectedSegments = [];
    },

    /** Update the simplify tolerance (clamped to 0.5–10). */
    setSimplifyTolerance(state, action: PayloadAction<number>) {
      state.simplifyTolerance = Math.min(10, Math.max(0.5, action.payload));
    },

    /**
     * Update the rounded-rectangle tool's corner radius (clamped to a
     * non-negative value). SH-SHAPE-101.
     */
    setRoundedRectangleCornerRadius(state, action: PayloadAction<number>) {
      state.roundedRectangleCornerRadiusInches = Math.max(0, action.payload);
    },

    /** Return every field to initial state. */
    resetEditor() {
      return initialState;
    },
  },
});

export const {
  setActiveTool,
  pushHistorySnapshot,
  undo,
  redo,
  updatePathData,
  updateBorder,
  setViewport,
  setZoom,
  setPan,
  setSnapToGrid,
  setBoundingBox,
  setEntryContext,
  setValidationWarnings,
  setSelectedSegments,
  toggleSelectedSegment,
  clearSelectedSegments,
  setSimplifyTolerance,
  setRoundedRectangleCornerRadius,
  resetEditor,
} = shapeEditorSlice.actions;

export { shapeEditorSlice };
export const shapeEditorReducer = shapeEditorSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

type StateWithShapeEditor = { shapeEditor: ShapeEditorState };

export const selectActiveTool = (state: StateWithShapeEditor): ShapeEditorTool =>
  state.shapeEditor.activeTool;

export const selectPathData = (state: StateWithShapeEditor): string | null =>
  state.shapeEditor.pathData;

/**
 * Whether `pathData` describes an actual drawing.
 *
 * Paper's `exportJSON()` returns `"[]"` for an empty project, which is a
 * perfectly valid non-null string — so a null check alone reads an empty canvas
 * as a finished shape. That is how an empty path used to reach add-to-cart and,
 * before PL-AUTH-FIX-003, lock a customer out of their account.
 *
 * Unparseable input counts as empty: the server rejects it either way, and the
 * editor should refuse before the customer is told the shape was saved.
 */
export function hasDrawnPath(pathData: string | null): boolean {
  if (pathData === null || pathData.trim() === '') return false;

  try {
    const parsed: unknown = JSON.parse(pathData);
    return Array.isArray(parsed) ? parsed.length > 0 : parsed !== null;
  } catch {
    return false;
  }
}

export const selectHasDrawnPath = (state: StateWithShapeEditor): boolean =>
  hasDrawnPath(state.shapeEditor.pathData);

export const selectViewport = (state: StateWithShapeEditor): ShapeEditorViewport =>
  state.shapeEditor.viewport;

export const selectBorder = (state: StateWithShapeEditor): ShapeEditorBorder =>
  state.shapeEditor.border;

export const selectBoundingBox = (state: StateWithShapeEditor): ShapeEditorBoundingBox =>
  state.shapeEditor.boundingBox;

export const selectEntryContext = (state: StateWithShapeEditor): EntryContext | null =>
  state.shapeEditor.entryContext;

export const selectValidationWarnings = (
  state: StateWithShapeEditor,
): ShapeEditorValidationWarning[] => state.shapeEditor.validationWarnings;

export const selectSelectedSegments = (state: StateWithShapeEditor): number[] =>
  state.shapeEditor.selectedSegments;

export const selectHistoryStack = (state: StateWithShapeEditor): string[] =>
  state.shapeEditor.historyStack;

export const selectCanUndo = (state: StateWithShapeEditor): boolean =>
  state.shapeEditor.historyStack.length > 0;

export const selectCanRedo = (state: StateWithShapeEditor): boolean =>
  state.shapeEditor.forwardStack.length > 0;

export const selectSimplifyTolerance = (state: StateWithShapeEditor): number =>
  state.shapeEditor.simplifyTolerance;

export const selectRoundedRectangleCornerRadiusInches = (
  state: StateWithShapeEditor,
): number => state.shapeEditor.roundedRectangleCornerRadiusInches;
